import { createContext, useCallback, useContext, useEffect, useMemo, useState,} from "react";
import { autoAppyUrl } from "src/config/autoAppyUrls";

import { getErrorMessage, getWorkspace, getWorkspaces,} from "../services/workspaceService";
import { useCurrentWorkspaceId } from "../hooks/useCurrentWorkspaceId";

import { AuthContext } from 'src/context/AuthContext';
import axios from 'src/common/axiosInstance';

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {

  const { currentUser, refreshSession } = useContext(AuthContext);

  // WORKSPACE LIST / CURRENT WORKSPACE
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentWorkspaceId, rememberWorkspaceId] =
    useCurrentWorkspaceId();

  const setCurrentWorkspaceId = useCallback(async (id) => {
    setError(null);
    try {
      await axios.post('/api/auth/workspace', { workspace_id: id });
      rememberWorkspaceId(id);
      await refreshSession();
    } catch (err) { setError(getErrorMessage(err, 'Unable to switch organisation.')); }
  }, [rememberWorkspaceId, refreshSession]);

  useEffect(() => {
    if (currentUser?.id) rememberWorkspaceId(currentUser.local_workspace_id || null);
  }, [currentUser?.id, currentUser?.local_workspace_id, rememberWorkspaceId]);

  // FETCH ALL WORKSPACES
  const fetchWorkspaces = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getWorkspaces();
      const workspaceList = Array.isArray(data) ? data : [];

      setWorkspaces(workspaceList);
      return workspaceList;
    } catch (err) {
      setError(
        getErrorMessage(err, "Unable to load workspaces.")
      );
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // INITIAL WORKSPACE LOAD (the provider wraps the whole app, so wait for a signed-in user)
  useEffect(() => {
    if (currentUser?.id) fetchWorkspaces();
  }, [currentUser?.id, fetchWorkspaces]);

  // RESOLVE CURRENT WORKSPACE
  useEffect(() => {
    if (loading || workspaces.length === 0) return;

    const stillExists = workspaces.some(
      (workspace) => workspace.id === currentWorkspaceId
    );

    if (currentWorkspaceId && stillExists) {
      return;
    }

    const firstActive =
      workspaces.find(
        (workspace) =>
          workspace.status?.toLowerCase() === "active"
      ) || workspaces[0];

    if (firstActive) {
      if (workspaces.length === 1) rememberWorkspaceId(firstActive.id);
    }
  }, [
    loading,
    workspaces,
    currentWorkspaceId,
    rememberWorkspaceId,
  ]);

  // CURRENT WORKSPACE
  const currentWorkspace = useMemo(
    () =>
      workspaces.find(
        (workspace) => workspace.id === currentWorkspaceId
      ) || null,
    [workspaces, currentWorkspaceId]
  );

  const openCreateModal = useCallback(() => {
    window.location.assign(autoAppyUrl('/organisation'));
  }, []);
  const openEditWorkspace = useCallback(workspace => {
    const query = workspace?.autoappy_workspace_id ? `?organisation=${encodeURIComponent(workspace.autoappy_workspace_id)}` : '';
    window.location.assign(autoAppyUrl(`/organisation${query}`));
  }, []);
  const fetchOne = useCallback(workspaceId => workspaceId ? getWorkspace(workspaceId) : null, []);

  // CONTEXT VALUE
  const value = useMemo(
    () => ({
      // Workspace list
      workspaces,
      loading,
      error,
      fetchWorkspaces,
      fetchOne,

      // Current workspace
      currentWorkspaceId,
      currentWorkspace,
      setCurrentWorkspaceId,

      // Create workspace
      openCreateModal,

      // Edit workspace
      openEditWorkspace,
    }),
    [
      workspaces,
      loading,
      error,
      fetchWorkspaces,
      fetchOne,

      currentWorkspaceId,
      currentWorkspace,
      setCurrentWorkspaceId,

      openCreateModal,

      openEditWorkspace,
    ]
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

// For shells that may render inside or outside a provider.
export function useOptionalWorkspace() {
  return useContext(WorkspaceContext);
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);

  if (!ctx) {
    throw new Error( "useWorkspace must be used within a <WorkspaceProvider>");
  }

  return ctx;
}
